import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Award, ArrowRight, CheckCircle, GraduationCap, 
  ChevronRight, Menu, X, BookOpen, ClipboardCheck,
  LineChart, Users, Shield, Bell, FileText, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Logo URL
const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const systemFeatures = [
    {
      icon: <ClipboardCheck className="w-5 h-5" />,
      title: "Мінез-құлық бақылау",
      description: "Оқушылардың тәртібін, үй тапсырмасын орындауын, сабаққа қатысуын нақты ұпайлар арқылы тіркеу"
    },
    {
      icon: <Award className="w-5 h-5" />,
      title: "Марапат жүйесі",
      description: "Жақсы мінез-құлық үшін марапат ұпайлары (merit), тәртіп бұзушылық үшін ескерту ұпайлары (demerit)"
    },
    {
      icon: <LineChart className="w-5 h-5" />,
      title: "Есептер мен статистика",
      description: "Ай сайынғы, тоқсандық есептер. Excel/PDF форматында жүктеу мүмкіндігі"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      title: "AI талдау",
      description: "Жасанды интеллект көмегімен оқушының мінез-құлық үлгісін талдау және ұсыныстар беру"
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Рөлдер жүйесі",
      description: "Әкімші, мұғалім, оқушы - әрқайсысына арналған жеке панель және құқықтар"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Мектеп оқшаулығы",
      description: "Әр мектептің деректері бір-бірінен толық оқшауланған. Тек өз мектебіңіздің ақпаратына қол жеткізесіз"
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Мұғалім ұпай қояды",
      description: "Мұғалім оқушыны таңдап, мінез-құлық түрін және ұпай санын белгілейді"
    },
    {
      step: "02", 
      title: "Жүйе автоматты есептейді",
      description: "Барлық ұпайлар жинақталып, оқушының жалпы көрсеткіші қалыптасады"
    },
    {
      step: "03",
      title: "Нәтижелер көрінеді",
      description: "Әкімші мен оқушы нақты уақытта статистиканы көре алады"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Merit Plus" className="w-9 h-9 rounded-lg" />
              <span className="text-lg font-semibold text-slate-800">Merit Plus</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#about" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Жүйе туралы
              </a>
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Мүмкіндіктер
              </a>
              <a href="#schools" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Мектептер
              </a>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Button 
                onClick={() => navigate('/qyzylordaer')}
                className="bg-teal-600 hover:bg-teal-700 text-white text-sm"
                data-testid="header-login-btn"
              >
                Жүйеге кіру
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 p-4">
            <nav className="flex flex-col gap-3">
              <a href="#about" className="text-slate-600 text-sm font-medium py-2">Жүйе туралы</a>
              <a href="#features" className="text-slate-600 text-sm font-medium py-2">Мүмкіндіктер</a>
              <a href="#schools" className="text-slate-600 text-sm font-medium py-2">Мектептер</a>
              <Button 
                onClick={() => navigate('/qyzylordaer')}
                className="bg-teal-600 hover:bg-teal-700 text-white w-full mt-2"
              >
                Жүйеге кіру
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5 leading-tight">
            Мектеп оқушыларының мінез-құлқын басқару жүйесі
          </h1>
          
          <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Merit Plus — оқушылардың тәртібін, жетістіктерін және қатысуын цифрлық түрде бақылауға арналған платформа. Мұғалімдер ұпай қояды, әкімшілер есептерді көреді, оқушылар өз нәтижелерін бақылайды.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button 
              size="lg"
              onClick={() => navigate('/qyzylordaer')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg"
              data-testid="hero-login-btn"
            >
              Жүйеге кіру
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              className="border-slate-300 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-50"
              data-testid="hero-learn-btn"
            >
              Толығырақ
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Жүйе қалай жұмыс істейді?
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Merit Plus мектеп ішіндегі тәртіп бақылауды жеңілдетеді
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-slate-50 rounded-xl p-6 h-full">
                  <div className="text-4xl font-bold text-teal-600/20 mb-3">{item.step}</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ChevronRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Негізгі мүмкіндіктер
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Мектеп әкімшілігі мен мұғалімдерге арналған құралдар
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {systemFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-teal-200 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-3 text-teal-600">
                  {feature.icon}
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Roles Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Пайдаланушы рөлдері
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Admin */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Әкімші</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Мұғалімдер мен оқушыларды тіркеу</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Мінез-құлық түрлерін баптау</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Есептерді көру және жүктеу</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Жалпы статистиканы бақылау</span>
                </li>
              </ul>
            </div>

            {/* Teacher */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Мұғалім</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Оқушыларға ұпай қою</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Бірнеше оқушыға бір уақытта ұпай беру</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Өз сыныбының статистикасын көру</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Жазбалар тарихын қарау</span>
                </li>
              </ul>
            </div>

            {/* Student */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-4">
                <GraduationCap className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Оқушы</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Өз ұпайларын көру</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Марапат/ескерту тарихы</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>Сынып орташасымен салыстыру</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span>AI талдау нәтижесін алу</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Schools Section */}
      <section id="schools" className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Қосылған мектептер
            </h2>
            <p className="text-slate-600">
              Merit Plus жүйесін қолданатын мектептер
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Qyzylorda School */}
            <div 
              onClick={() => navigate('/qyzylordaer')}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer group"
              data-testid="qyzylorda-school-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center text-white">
                  <GraduationCap className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Qyzylorda BIL мектебі</h3>
                  <p className="text-sm text-slate-500">Қызылорда қаласы</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>14 сынып</span>
                  <span>46 мұғалім</span>
                  <span>208 оқушы</span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
              </div>
            </div>

            {/* Shymkent School */}
            <div 
              onClick={() => navigate('/shymkenter')}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
              data-testid="shymkent-school-card"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
                  <GraduationCap className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Shymkent BIL мектебі</h3>
                  <p className="text-sm text-slate-500">Шымкент қаласы</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>1 сынып</span>
                  <span>Жаңа қосылған</span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Merit Plus" className="w-9 h-9 rounded-lg" />
                <span className="text-lg font-semibold">Merit Plus</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Мектеп оқушыларының мінез-құлқын цифрлық түрде бақылау және басқару жүйесі.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm">Жүйе</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="#about" className="block hover:text-white transition-colors">Жүйе туралы</a>
                <a href="#features" className="block hover:text-white transition-colors">Мүмкіндіктер</a>
                <a href="#schools" className="block hover:text-white transition-colors">Мектептер</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-sm">Байланыс</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>info@meritplusbil.org</p>
                <p>+7 (7172) 123-456</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center text-sm text-slate-500">
            <p>© 2026 Merit Plus. Барлық құқықтар қорғалған.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
